import { JSX, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { Alert, AlertTitle, Box, Chip, IconButton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useIncidentsQuery } from "src/api/incidentQueries.api";
import { formatLocalTimeRangeParts } from "src/lib/date.helper";
import { type IncidentDto } from "src/types/incidentDto";

const incidentKey = (item: IncidentDto): string => `${item.startTS}|${item.title}`;

const styles = {
  box: {
    position: "fixed",
    top: 24,
    right: 24,
    zIndex: 9999,
    maxWidth: 480,
    width: "calc(100vw - 48px)",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  chipBox: { display: "flex", flexWrap: "wrap", mt: 1 },
  chipIcon: {color: "inherit"},
  chipPart: { whiteSpace: "nowrap" },
  chip: {
    color: "inherit",
    borderColor: "rgba(0,0,0,0.2)",
    height: "auto",
    "& .MuiChip-label": { display: "block", whiteSpace: "normal", py: 0.5 },
  }
};

function getAlertProps(item: IncidentDto): { severity: "info" | "error"; icon: JSX.Element | undefined } {
  if (item.type === "maintenance") {
    return { severity: "info", icon: <AccessTimeIcon fontSize="inherit" /> };
  }
  return { severity: "error", icon: undefined };
}

function OutageAlerts(): JSX.Element | null {
  const { t } = useTranslation();
  const { data: incidents = [] } = useIncidentsQuery();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = incidents.filter(
    (item) => !dismissed.has(incidentKey(item)) && new Date(item.endTS) >= new Date(item.startTS)
  );
  if (visible.length === 0) return null;

  const dismiss = (key: string): void => {
    setDismissed((prev) => new Set([...prev, key]));
  };

  return (
    <Box sx={styles.box}>
      <Stack spacing={1}>
        {visible.map((item) => {
          const key = incidentKey(item);
          const { severity, icon } = getAlertProps(item);
          const { start, end } = formatLocalTimeRangeParts(item.startTS, item.endTS, t("expected"));

          return (
            <Alert
              key={key}
              severity={severity}
              icon={icon}
              action={
                <IconButton size="small" color="inherit" onClick={() => dismiss(key)} aria-label="dismiss">
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <AlertTitle>{item.title}</AlertTitle>
              {item.message}
              <Box sx={styles.chipBox}>
                <Chip
                  icon={<DateRangeIcon sx={styles.chipIcon} />}
                  label={
                    <>
                      <Box component="span" sx={styles.chipPart}>
                        {start}
                      </Box>{" "}
                      <Box component="span" sx={styles.chipPart}>
                        {end}
                      </Box>
                    </>
                  }
                  size="small"
                  sx={styles.chip}
                />
              </Box>
            </Alert>
          );
        })}
      </Stack>
    </Box>
  );
}

export default OutageAlerts;
